import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import VideoEmailCapture, { useVideoEmailPopup } from '../components/VideoEmailCapture';
import { Play, Filter, Search, Clock, Eye, X, ChevronRight, Video, BookOpen, Utensils, Users, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoGallery = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { showPopup, setShowPopup, triggerType } = useVideoEmailPopup({
    enableExitIntent: true,
    enableTimed: true,
    timedDelay: 3000
  });

  const categories = [
    { id: 'all', name: 'All Videos', icon: Video },
    { id: 'tutorials', name: 'Tutorials', icon: BookOpen },
    { id: 'recipes', name: 'Recipes', icon: Utensils },
    { id: 'customer-stories', name: 'Customer Stories', icon: Users },
    { id: 'tips-tricks', name: 'Tips & Tricks', icon: Sparkles }
  ];

  // Default videos if none in database
  const defaultVideos = [
    {
      id: '1',
      title: 'Getting Started with Your D-Freeze Freeze Dryer',
      description: 'A complete walkthrough of setting up your new D-Freeze freeze dryer and running your first batch.',
      thumbnail: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'tutorials',
      duration: '12:34',
      views: 15420,
      featured: true
    },
    {
      id: '2',
      title: 'Freeze Drying Fruits: Complete Guide',
      description: 'Learn how to freeze dry strawberries, bananas, apples, and more with perfect results every time.',
      thumbnail: 'https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'tutorials',
      duration: '18:22',
      views: 12350
    },
    {
      id: '3',
      title: 'Making Freeze Dried Candy at Home',
      description: 'The viral trend explained! Make puffy, crunchy freeze dried candy that everyone loves.',
      thumbnail: 'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'recipes',
      duration: '8:45',
      views: 45200,
      featured: true
    },
    {
      id: '4',
      title: 'Customer Story: The Johnson Family Farm',
      description: 'See how the Johnsons preserve their entire garden harvest with D-Freeze freeze dryers.',
      thumbnail: 'https://images.unsplash.com/photo-1500076656116-558758c991c1?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'customer-stories',
      duration: '6:18',
      views: 8920
    },
    {
      id: '5',
      title: 'Emergency Food Storage Tips',
      description: 'Expert tips on building a 1-year emergency food supply using freeze dried foods.',
      thumbnail: 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'tips-tricks',
      duration: '14:56',
      views: 22100
    },
    {
      id: '6',
      title: 'Freeze Dried Pet Treats Your Dog Will Love',
      description: 'Make healthy, preservative-free treats for your furry friends.',
      thumbnail: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'recipes',
      duration: '10:12',
      views: 9800
    },
    {
      id: '7',
      title: 'Maintenance & Cleaning Your Freeze Dryer',
      description: 'Keep your machine running perfectly with these maintenance tips.',
      thumbnail: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'tutorials',
      duration: '9:33',
      views: 7650
    },
    {
      id: '8',
      title: 'Customer Story: Starting a Freeze Dry Business',
      description: 'How Sarah turned her hobby into a thriving online business.',
      thumbnail: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      category: 'customer-stories',
      duration: '11:28',
      views: 18400
    }
  ];

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/videos`);
        if (res.data && res.data.length > 0) {
          setVideos(res.data);
        } else {
          setVideos(defaultVideos);
        }
      } catch (e) {
        setVideos(defaultVideos);
      } finally {
        setLoading(false);
      }
    };
    loadVideos();
  }, []);

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredVideos = videos.filter(v => v.featured);

  const formatViews = (views) => {
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views;
  };

  const getCategoryIcon = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.icon : Video;
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero */}
      <div className="pt-44 pb-16 bg-gradient-to-br from-[#003456] via-[#002845] to-[#002B47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#CE8946] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          <div className="w-16 h-16 bg-[#CE8946] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Play className="h-8 w-8 text-[#0a0203]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Video Gallery
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Watch tutorials, recipes, customer stories, and tips to get the most out of your D-Freeze freeze dryer.
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search videos..."
              className="pl-12 py-6 text-lg bg-white/10 border-white/20 text-white placeholder-white/50 focus:bg-white focus:text-[#0a0203] focus:placeholder-gray-400"
              data-testid="video-search"
            />
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="sticky top-[136px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === category.id
                      ? 'bg-[#003456] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          {/* Featured Videos */}
          {selectedCategory === 'all' && featuredVideos.length > 0 && !searchQuery && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Featured Videos</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {featuredVideos.slice(0, 2).map((video) => {
                  const CategoryIcon = getCategoryIcon(video.category);
                  return (
                    <div
                      key={video.id}
                      onClick={() => setSelectedVideo(video)}
                      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer group"
                      data-testid={`featured-video-${video.id}`}
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                            <Play className="h-8 w-8 text-[#003456] ml-1" />
                          </div>
                        </div>
                        <div className="absolute bottom-3 right-3 bg-black/80 text-white text-xs px-2 py-1 rounded">
                          {video.duration}
                        </div>
                        <div className="absolute top-3 left-3 bg-[#CE8946] text-[#0a0203] text-xs font-bold px-3 py-1 rounded">
                          FEATURED
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-sm text-[#003456] mb-2">
                          <CategoryIcon className="h-4 w-4" />
                          <span>{getCategoryName(video.category)}</span>
                        </div>
                        <h3 className="text-xl font-bold text-[#0a0203] mb-2 group-hover:text-[#003456] transition-colors">
                          {video.title}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2">{video.description}</p>
                        <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {formatViews(video.views)} views
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All Videos Grid */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#0a0203]">
                {selectedCategory === 'all' ? 'All Videos' : getCategoryName(selectedCategory)}
              </h2>
              <span className="text-gray-500">{filteredVideos.length} videos</span>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                    <div className="aspect-video bg-gray-200"></div>
                    <div className="p-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredVideos.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-6">
                {filteredVideos.map((video) => {
                  const CategoryIcon = getCategoryIcon(video.category);
                  return (
                    <div
                      key={video.id}
                      onClick={() => setSelectedVideo(video)}
                      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group"
                      data-testid={`video-card-${video.id}`}
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                            <Play className="h-6 w-6 text-[#003456] ml-0.5" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                          {video.duration}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 text-xs text-[#003456] mb-2">
                          <CategoryIcon className="h-3 w-3" />
                          <span>{getCategoryName(video.category)}</span>
                        </div>
                        <h3 className="font-semibold text-[#0a0203] mb-1 group-hover:text-[#003456] transition-colors line-clamp-2">
                          {video.title}
                        </h3>
                        <p className="text-gray-500 text-sm line-clamp-2">{video.description}</p>
                        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {formatViews(video.views)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {video.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Video className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#0a0203] mb-2">No videos found</h3>
                <p className="text-gray-500 mb-4">Try a different search term or category</p>
                <Button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }} className="bg-[#003456]">
                  Clear Filters
                </Button>
              </div>
            )}
          </div>

          {/* Back to Resources */}
          <div className="mt-12 text-center">
            <Link to="/resources" className="inline-flex items-center gap-2 text-[#003456] font-medium hover:underline">
              <ChevronRight className="h-4 w-4 rotate-180" />
              Back to Resources
            </Link>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-[#0a0203] line-clamp-1">{selectedVideo.title}</h3>
              <button 
                onClick={() => setSelectedVideo(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
                data-testid="close-video-modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="aspect-video">
              <iframe
                src={selectedVideo.video_url}
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">{selectedVideo.description}</p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  {formatViews(selectedVideo.views)} views
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {selectedVideo.duration}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />

      {/* Email Capture Popup */}
      {showPopup && (
        <VideoEmailCapture onClose={() => setShowPopup(false)} triggerType={triggerType} />
      )}
    </div>
  );
};

export default VideoGallery;
